import {
  Button,
  ButtonGroup,
  Divider,
  GridItem,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spacer,
  Tooltip,
  useColorModeValue,
  useToken,
} from '@chakra-ui/react';
import {
  faAngleDown,
  faBuilding,
  faClipboardCheck,
  faCut,
  faLayerGroup,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { find, isEmpty, isEqual, sum } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { useFlag } from '@unleash/proxy-client-react';
import { useTranslation } from 'react-i18next';
import { CustomElementClass } from '../../../domain/geometry/algorithm-map';
import {
  GetProjectSettingsDocument,
  Model,
  ModelProcessingStatus,
  ViewSection,
} from '../../../gql/graphql';
import { ResolvedDbIdsReturn } from '../../../hooks/order';
import { UserType, useUserTenant } from '../../../services/auth-info';
import { CommentOverlay } from '../../comments/CommentOverlay';
import CommentsPopover from '../../comments/CommentsPopover';
import CommentsProvider from '../../comments/useComments';
import CategoriesMenuListCheckBoxes, {
  possibleCategories,
} from '../../orderContractor/CategoriesMenuListCheckBoxes';
import LayersMenuListCheckBoxes, {
  possibleLayerTypes,
} from '../../orderContractor/LayersMenuListCheckBoxes';
import OrderStatusMenuListCheckBoxes, {
  possibleOrderStatuses,
} from '../../orderContractor/OrderStatusMenuListCheckBoxes';
import { CommentsPopoverProvider } from '../CommentsPopoverProvider';
import { DbIdsPerModel, useViewer } from '../ForgeViewer';
import GhostModeButton from '../GhostModeButton';
import OrthographicModeButton from '../OrtographicModeButton';
import ShapeLabelsButton from '../ShapeLabelsButton';
import SparkelIcon from '../icon/SparkelIcon';
import { useViewerMode, ViewerMode } from '../viewer-mode';
import { useViewManager } from '../ViewManagerContext';
import { invalidateMagicShapeIntersectionCache } from '../../orderContractor/shapes/shape-intersection';
import { ReactComponent as xPlaneIcon } from './../../../assets/icons/xplaneicon.svg';
import { ReactComponent as yPlaneIcon } from './../../../assets/icons/yplaneicon.svg';
import { ReactComponent as zPlaneIcon } from './../../../assets/icons/zplaneicon.svg';
import { ElementSearchBar } from './ElementSearchBar';
import SavedViewsMenu, { getCameraPosition } from './savedViews/SavedViewsMenu';
import HandleInitialState from './HandleInitialState';
import IsolatedElementsButton from './IsolatedElementsButton';
import HiddenElementsButton from './HiddenElementsButton';
import MeasureToolButton from './MeasureToolButton';
import { UnitTypeToUnitSymbol } from 'src/utils/unit-utils';
import { hasDbIds } from 'src/services/viewer-services';

export type State = {
  selectedCategories: Set<string>;
  selectedLayers: Set<string>;
  selectedElementOrderStatuses: Set<string>;
  activeModels: string[];
};

enum ActionKind {
  ResetView = 'RESETVIEW',
  NewSelectedLayers = 'NEWSELECTEDLAYERS',
  NewSelectedCategories = 'NEWSELECTEDCATEGORIES',
  NewSelectedOrderStatuses = 'NEWSELECTEDORDERSTATUSES',
}

type Action = {
  type: ActionKind;
  payload?: Iterable<string>;
};

type ProjectPathParams = {
  projectId: string;
};
interface ForgeViewerInteractionsProps {
  resolvedDbIds: ResolvedDbIdsReturn;
  models: Pick<
    Model,
    | 'status'
    | 'forgeUrn'
    | 'ifcCategories'
    | 'revitCategories'
    | 'ifcLayers'
    | 'revitLevels'
  >[];
}

type UseSectionReturn = [
  ViewSection | null,
  (section: ViewSection | null) => void
];

const useSections = (): UseSectionReturn => {
  const [section, setSection] = useState<ViewSection | null>(null);

  const { viewer } = useViewer();

  const sectionExtension = useMemo(() => {
    if (!viewer) {
      return null;
    } else {
      return viewer.getExtension('Autodesk.Section');
    }
  }, [viewer]);

  useEffect(() => {
    if (!sectionExtension) {
      return;
    }

    if (section === null) {
      sectionExtension.deactivate();
    } else {
      sectionExtension.activate(section.toLowerCase());
    }
  }, [sectionExtension, section]);

  return [section, setSection];
};

/* eslint-disable complexity */
export default function ForgeViewerInteractions({
  models,
  resolvedDbIds,
}: ForgeViewerInteractionsProps): ReactElement | null {
  const [gray600] = useToken('colors', ['gray.600']);
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const { viewerMode } = useViewerMode();

  const {
    viewer,
    loadedModels,
    shapesManager: { loadedShapeModels, renderedShapes },
    isAllModelsLoaded,
    visibilityManager: {
      setFilteredElements,
      reset: resetVisibility,
      setIsolatedElements,
      setHiddenElements,
      hiddenElements,
      isolatedElements,
      hiddenModelUrns,
    },
  } = useViewer();
  const [activeSection, setActiveSection] = useSections();

  const { projectId } = useParams<
    keyof ProjectPathParams
  >() as ProjectPathParams;

  const { t } = useTranslation('project');

  const enableUnitSettings = useFlag('enable-unit-settings');

  const [{ data: getProjectSettings }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: { projectId },
  });
  const measureExtension = useMemo(() => {
    const extension = viewer?.getExtension(
      'Autodesk.Measure'
    ) as Autodesk.Extensions.Measure.MeasureExtension | null;

    const unitSettings = getProjectSettings?.project?.settings?.unitSettings;
    const lengthUnitSetting = unitSettings?.length;
    extension?.setUnits(
      enableUnitSettings && lengthUnitSetting?.units
        ? UnitTypeToUnitSymbol(lengthUnitSetting.units)
        : 'm'
    );
    extension?.setPrecision(3);
    return extension;
  }, [
    enableUnitSettings,
    getProjectSettings?.project?.settings?.unitSettings,
    viewer,
  ]);

  const [isMeasureActive, setMeasureActive] = useState(false);

  const currentModels = useMemo(
    () =>
      models
        .filter((model) => model.status === ModelProcessingStatus.Complete)
        .map((model) => model.forgeUrn)
        .filter((urn): urn is string => !!urn),
    [models]
  );

  const initialState: State = {
    // NOTE: jonasws 2022-06-28: Figure out union/labels here
    selectedCategories: new Set(
      possibleCategories(models, t).flatMap((category) => category.friendlyName)
    ),
    selectedLayers: new Set(possibleLayerTypes(models)),
    selectedElementOrderStatuses: new Set(possibleOrderStatuses),
    activeModels: currentModels,
  };

  const initStateRef = useRef<State>(initialState);

  const reducer = (state: State, action: Action) => {
    let newState = { ...state };

    switch (action.type) {
      case ActionKind.ResetView:
        return initialState;
      case ActionKind.NewSelectedLayers:
        newState.selectedLayers = action.payload
          ? new Set(action.payload)
          : new Set();
        break;
      case ActionKind.NewSelectedCategories:
        newState.selectedCategories = action.payload
          ? new Set(action.payload)
          : new Set();
        break;
      case ActionKind.NewSelectedOrderStatuses:
        newState.selectedElementOrderStatuses = action.payload
          ? new Set(action.payload)
          : new Set();
        break;
      default:
        throw new Error();
    }

    return newState;
  };

  const [viewerFilterState, dispatchViewerFilterAction] = useReducer(
    reducer,
    initialState
  );

  useEffect(() => {
    if (!isEqual(currentModels, viewerFilterState.activeModels)) {
      dispatchViewerFilterAction({ type: ActionKind.ResetView });
    }
  }, [currentModels, viewerFilterState.activeModels]);

  useEffect(() => {
    invalidateMagicShapeIntersectionCache();
  }, [
    hiddenElements,
    isolatedElements,
    hiddenModelUrns,
    viewerFilterState.selectedCategories,
    viewerFilterState.selectedLayers,
    viewerFilterState.selectedElementOrderStatuses,
  ]);

  useEffect(() => {
    if (!isEmpty(isolatedElements)) {
      dispatchViewerFilterAction({ type: ActionKind.ResetView });
    }
  }, [isolatedElements]);

  useEffect(() => {
    const isolateFilteredElements = async () => {
      if (
        !isEmpty(loadedModels) &&
        isAllModelsLoaded &&
        !resolvedDbIds.isLoading
      ) {
        if (
          viewerFilterState.selectedCategories.size ===
            initStateRef.current.selectedCategories.size &&
          viewerFilterState.selectedLayers.size ===
            initStateRef.current.selectedLayers.size &&
          viewerFilterState.selectedElementOrderStatuses.size ===
            initStateRef.current.selectedElementOrderStatuses.size
        ) {
          setFilteredElements({});
          return;
        }

        const selectedCategories = possibleCategories(models, t)
          .filter((friendlyCategory) =>
            viewerFilterState.selectedCategories.has(
              friendlyCategory.friendlyName
            )
          )
          .flatMap((category) => category.modernValues);

        const selectedCategorySet = new Set(selectedCategories);
        const selectedStatuses = viewerFilterState.selectedElementOrderStatuses;

        const elementsToIsolate: DbIdsPerModel = {};

        const modelEntries = Object.entries(loadedModels).filter(
          ([modelUrn]) => !hiddenModelUrns.includes(modelUrn)
        );

        // Resolve per-model candidates in parallel
        const perModelPromises = modelEntries.map(async ([modelUrn, model]) => {
          const [ifcClassDbIds, revitDbIds] = await Promise.all([
            model.findProperty('IfcClass'),
            model.findProperty('Level'),
          ]);

          const [elementsWithIfcClass, elementsWithRevitLevel] =
            await Promise.all([
              new Promise<Autodesk.Viewing.PropertyResult[]>((resolve) => {
                model.getBulkProperties2(
                  ifcClassDbIds,
                  {
                    propFilter: ['IfcClass', 'Layer'],
                  },
                  resolve
                );
              }),
              new Promise<Autodesk.Viewing.PropertyResult[]>((resolve) => {
                model.getBulkProperties2(
                  revitDbIds,
                  {
                    propFilter: [
                      'Level',
                      'Category',
                      'Base Constraint',
                      'Base Level',
                    ],
                  },
                  resolve
                );
              }),
            ]);

          const candidateDbIds: number[] = [];

          for (const { dbId, properties } of elementsWithIfcClass) {
            const ifcClass: Autodesk.Viewing.Property | undefined = find(
              properties,
              ['attributeName', 'IfcClass']
            );
            const layer: Autodesk.Viewing.Property | undefined = find(
              properties,
              ['attributeName', 'Layer']
            );

            if (
              selectedCategorySet.has(`${ifcClass?.displayValue}`) &&
              viewerFilterState.selectedLayers.has(`${layer?.displayValue}`)
            ) {
              candidateDbIds.push(dbId);
            }
          }

          for (const { dbId, properties } of elementsWithRevitLevel) {
            const revitCategory: Autodesk.Viewing.Property | undefined = find(
              properties,
              ['displayCategory', '__category__']
            );

            let revitLevel: Autodesk.Viewing.Property | undefined = find(
              properties,
              (p: Autodesk.Viewing.Property) =>
                p.displayCategory === 'Constraints' &&
                p.attributeName === 'Level'
            );

            let revitBaseConstraint: Autodesk.Viewing.Property | undefined =
              find(
                properties,
                (p: Autodesk.Viewing.Property) =>
                  p.displayCategory === 'Constraints' &&
                  p.attributeName === 'Base Constraint'
              );

            let revitBaseLevel: Autodesk.Viewing.Property | undefined = find(
              properties,
              (p: Autodesk.Viewing.Property) =>
                p.displayCategory === 'Constraints' &&
                p.attributeName === 'Base Level'
            );

            revitLevel =
              revitLevel?.displayCategory === 'Constraints'
                ? revitLevel
                : undefined;

            revitBaseConstraint =
              revitBaseConstraint?.displayCategory === 'Constraints'
                ? revitBaseConstraint
                : undefined;

            revitBaseLevel =
              revitBaseLevel?.displayCategory === 'Constraints'
                ? revitBaseLevel
                : undefined;

            if (
              selectedCategorySet.has(`${revitCategory?.displayValue}`) &&
              (viewerFilterState.selectedLayers.has(
                `${revitLevel?.displayValue}`
              ) ||
                viewerFilterState.selectedLayers.has(
                  `${revitBaseConstraint?.displayValue}`
                ) ||
                viewerFilterState.selectedLayers.has(
                  `${revitBaseLevel?.displayValue}`
                ))
            ) {
              candidateDbIds.push(dbId);
            }
          }

          // Apply order status filtering in bulk
          let allowedDbIds = candidateDbIds;
          if (candidateDbIds.length > 0 && selectedStatuses.size > 0) {
            const includeUnlinked = selectedStatuses.has('UNLINKED');

            // Find all orders that intersect with any candidate dbIds once
            const matchingOrders = resolvedDbIds.findOrdersMatchingDbIds([
              { modelUrn, dbIds: candidateDbIds },
            ]);

            const ordersByStatus = new Map<string, string[]>();
            for (const order of matchingOrders) {
              const arr = ordersByStatus.get(order.status) || [];
              arr.push(order.id);
              ordersByStatus.set(order.status, arr);
            }

            const linkedDbIdSet = new Set<number>();
            const allowedLinkedDbIdSet = new Set<number>();
            const candidateDbIdSet = new Set<number>(candidateDbIds);

            // Build linked sets per selected status efficiently
            for (const [status, orderIds] of ordersByStatus.entries()) {
              const isStatusSelected = selectedStatuses.has(status);
              if (!isStatusSelected && !includeUnlinked) {
                // Skip statuses we don't need
                continue;
              }
              for (const orderId of orderIds) {
                const dbIdsForOrder = resolvedDbIds.getDbIdsForOrder(orderId);
                for (const { modelUrn: urn, dbIds } of dbIdsForOrder) {
                  if (urn !== modelUrn) continue;
                  for (const id of dbIds) {
                    if (candidateDbIdSet.has(id)) {
                      linkedDbIdSet.add(id);
                      if (isStatusSelected) {
                        allowedLinkedDbIdSet.add(id);
                      }
                    }
                  }
                }
              }
            }

            const result: number[] = [];
            const candidateSet = candidateDbIdSet;
            if (selectedStatuses.size === 1 && includeUnlinked) {
              // Only UNLINKED: keep those not linked
              for (const id of candidateSet) {
                if (!linkedDbIdSet.has(id)) result.push(id);
              }
            } else if (!includeUnlinked) {
              // Only linked statuses
              for (const id of candidateSet) {
                if (allowedLinkedDbIdSet.has(id)) result.push(id);
              }
            } else {
              // Mixed: union of linked with selected statuses and unlinked
              for (const id of candidateSet) {
                if (!linkedDbIdSet.has(id) || allowedLinkedDbIdSet.has(id)) {
                  result.push(id);
                }
              }
            }

            allowedDbIds = result;
          }

          elementsToIsolate[modelUrn] = allowedDbIds;
        });

        await Promise.all(perModelPromises);

        // Handle polygon shapes
        for (const modelUrn of Object.keys(loadedShapeModels)) {
          const dbIds = renderedShapes[modelUrn].map((shape) => shape.dbId);
          if (selectedCategorySet.has(CustomElementClass.PolygonShape)) {
            let allowed = dbIds;
            if (dbIds.length > 0 && selectedStatuses.size > 0) {
              const includeUnlinked = selectedStatuses.has('UNLINKED');
              const matchingOrders = resolvedDbIds.findOrdersMatchingDbIds([
                { modelUrn, dbIds },
              ]);
              const ordersByStatus = new Map<string, string[]>();
              for (const order of matchingOrders) {
                const arr = ordersByStatus.get(order.status) || [];
                arr.push(order.id);
                ordersByStatus.set(order.status, arr);
              }

              const linkedDbIdSet = new Set<number>();
              const allowedLinkedDbIdSet = new Set<number>();
              const dbIdSet = new Set<number>(dbIds);
              for (const [status, orderIds] of ordersByStatus.entries()) {
                const isStatusSelected = selectedStatuses.has(status);
                if (!isStatusSelected && !includeUnlinked) continue;
                for (const orderId of orderIds) {
                  const dbIdsForOrder = resolvedDbIds.getDbIdsForOrder(orderId);
                  for (const {
                    modelUrn: urn,
                    dbIds: orderDbIds,
                  } of dbIdsForOrder) {
                    if (urn !== modelUrn) continue;
                    for (const id of orderDbIds) {
                      if (dbIdSet.has(id)) {
                        linkedDbIdSet.add(id);
                        if (isStatusSelected) allowedLinkedDbIdSet.add(id);
                      }
                    }
                  }
                }
              }

              const result: number[] = [];
              const candidateSet = new Set(dbIds);
              if (selectedStatuses.size === 1 && includeUnlinked) {
                for (const id of candidateSet) {
                  if (!linkedDbIdSet.has(id)) result.push(id);
                }
              } else if (!includeUnlinked) {
                for (const id of candidateSet) {
                  if (allowedLinkedDbIdSet.has(id)) result.push(id);
                }
              } else {
                for (const id of candidateSet) {
                  if (!linkedDbIdSet.has(id) || allowedLinkedDbIdSet.has(id)) {
                    result.push(id);
                  }
                }
              }
              allowed = result;
            }
            elementsToIsolate[modelUrn] = allowed;
          }
        }

        setFilteredElements(elementsToIsolate);
      }
    };

    isolateFilteredElements();
  }, [
    models,
    isAllModelsLoaded,
    loadedModels,
    resolvedDbIds,
    viewerFilterState.selectedElementOrderStatuses,
    viewerFilterState.selectedCategories,
    viewerFilterState.selectedLayers,
    setFilteredElements,
    loadedShapeModels,
    renderedShapes,
    initialState.selectedCategories.size,
    initialState.selectedLayers.size,
    initialState.selectedElementOrderStatuses.size,
    t,
    hiddenModelUrns,
  ]);

  const handleHomeClick = () => {
    if (viewer) {
      setActiveSection(null);
      viewer.navigation.setRequestHomeView(true);
      measureExtension?.deleteMeasurements();
      measureExtension?.deactivate();
      setMeasureActive(false);
      resetVisibility();
    }

    dispatchViewerFilterAction({ type: ActionKind.ResetView });
  };

  useEffect(() => {
    if (viewer) {
      const manager = viewer.getHotkeyManager();
      const ESCAPE_KEY_CODE = 27;
      if (manager) {
        const hotKeyEsc = {
          keycodes: [ESCAPE_KEY_CODE],
          onPress: () => true,
          onRelease: () => {
            measureExtension?.deleteMeasurements();
            measureExtension?.deactivate();
            setMeasureActive(false);
            viewer.setAggregateSelection([]);
            return true;
          },
        };
        manager.pushHotkeys('Escape', [hotKeyEsc]);
        manager.popHotkeys('Autodesk.Escape');
      }
      return () => void manager.popHotkeys('Escape');
    }
  }, [setActiveSection, isAllModelsLoaded, measureExtension, viewer]);

  const showResetFilterButton = !isEqual(viewerFilterState, initialState);

  const numHiddenElements = sum(
    Object.values(hiddenElements).map((v) => v.length)
  );
  const numIsolatedElements = sum(
    Object.values(isolatedElements).map((v) => v.length)
  );

  const setCategories = useCallback(
    (categories: Iterable<string>) =>
      dispatchViewerFilterAction({
        type: ActionKind.NewSelectedCategories,
        payload: categories,
      }),
    []
  );

  const setSelectedLayers = useCallback(
    (layerTypes: Iterable<string>) =>
      dispatchViewerFilterAction({
        type: ActionKind.NewSelectedLayers,
        payload: layerTypes,
      }),
    []
  );

  const setOrderStatuses = useCallback((orderStatuses: Iterable<string>) => {
    dispatchViewerFilterAction({
      type: ActionKind.NewSelectedOrderStatuses,
      payload: orderStatuses,
    });
  }, []);

  const {
    setCurrentView,
    setSuccessfullyRetrievedCurrentView: setSuccess,
    isGettingCurrentView: shouldSetView,
  } = useViewManager();

  useEffect(() => {
    // TODO: Handle errors
    if (shouldSetView && viewer) {
      setCurrentView({
        cameraPosition: getCameraPosition(viewer),
        categories: Array.from(viewerFilterState.selectedCategories),
        layers: Array.from(viewerFilterState.selectedLayers),
        statuses: Array.from(viewerFilterState.selectedElementOrderStatuses),
        section: activeSection,
        hiddenElements: hasDbIds(hiddenElements)
          ? Object.entries(hiddenElements).map(([modelUrn, dbIds]) => ({
              modelUrn,
              dbIds,
            }))
          : null,
        isolatedElements: hasDbIds(isolatedElements)
          ? Object.entries(isolatedElements).map(([modelUrn, dbIds]) => ({
              modelUrn,
              dbIds,
            }))
          : null,
        hiddenModels: hiddenModelUrns,
      });
      setSuccess(true);
    }
  }, [
    activeSection,
    isolatedElements,
    setSuccess,
    setCurrentView,
    shouldSetView,
    viewerFilterState.selectedCategories,
    viewerFilterState.selectedElementOrderStatuses,
    viewerFilterState.selectedLayers,
    hiddenElements,
    viewer,
    hiddenModelUrns,
  ]);

  const user = useUserTenant();
  const isContractor = user.userDetails.userType === UserType.Contractor;

  const commentsPopoverAttributes = {
    selectedCategories: viewerFilterState.selectedCategories,
    selectedLayers: viewerFilterState.selectedLayers,
    selectedOrderStatuses: viewerFilterState.selectedElementOrderStatuses,
    activeSection,
    setCategories,
    setSelectedLayers,
    setOrderStatuses,
    setActiveSection,
    projectId: projectId,
  };

  const activeIconColor = useColorModeValue('teal.500', 'teal.200');
  const isCategoriesFiltered =
    viewerFilterState.selectedCategories.size !==
    possibleCategories(models, t).length;
  const isLevelsFiltered =
    viewerFilterState.selectedLayers.size !== possibleLayerTypes(models).size;
  const isStatusFiltered =
    viewerFilterState.selectedElementOrderStatuses.size !==
    possibleOrderStatuses.length;

  return !isEmpty(loadedModels) && isAllModelsLoaded ? (
    <GridItem
      area="viewer-interactions"
      // Quickfix: Render this component with display none when in sheets mode to prevent
      // all the state from being reset when switching modes
      display={viewerMode === ViewerMode.Models ? 'flex' : 'none'}
      pointerEvents="none"
      paddingTop={2}
      zIndex={3}
      // This is a hack to make sure the buttons are clickable, but not the spacer, which allows the user to click through to the viewer
      sx={{
        '> *:not(:empty)': {
          pointerEvents: 'auto',
        },
        '@media (max-width: 1200px)': {
          paddingRight: 2,
        },
      }}
    >
      <ButtonGroup>
        <SavedViewsMenu
          homeFallbackHandler={handleHomeClick}
          selectedCategories={viewerFilterState.selectedCategories}
          selectedLayers={viewerFilterState.selectedLayers}
          selectedOrderStatuses={viewerFilterState.selectedElementOrderStatuses}
          activeSection={activeSection}
          setCategories={setCategories}
          setSelectedLayers={setSelectedLayers}
          setOrderStatuses={setOrderStatuses}
          setActiveSection={setActiveSection}
        />
        <HandleInitialState
          setCategories={setCategories}
          setSelectedLayers={setSelectedLayers}
          setOrderStatuses={setOrderStatuses}
        />
        {isContractor ? (
          <>
            {
              //Todo: Dual provider no good
            }
            <CommentsPopoverProvider attributes={commentsPopoverAttributes}>
              <CommentsProvider>
                <>
                  {viewer ? <CommentOverlay viewer={viewer} /> : null}
                  <CommentsPopover />
                </>
              </CommentsProvider>
            </CommentsPopoverProvider>
          </>
        ) : null}

        <Divider borderColor={borderColor} orientation="vertical" />
        <GhostModeButton />
        <ShapeLabelsButton />
        <OrthographicModeButton />
        <MeasureToolButton
          isMeasureActive={isMeasureActive}
          onToggleMeasure={() => {
            isMeasureActive
              ? measureExtension?.deactivate()
              : measureExtension?.activate('distance');
            setMeasureActive((active) => !active);
          }}
        />
        {activeSection !== null ? (
          <Tooltip label={t('viewer-interactions.clear-section')}>
            <Button
              aria-label="Clear section"
              borderRadius={'3xl'}
              colorScheme="gray"
              variant="solid-no-transparent"
              size="xs"
              onClick={() => void setActiveSection(null)}
            >
              <SparkelIcon icon={faCut} />
            </Button>
          </Tooltip>
        ) : (
          <Menu isLazy>
            <Tooltip label={t('viewer-interactions.section')}>
              <MenuButton
                as={Button}
                borderRadius={'3xl'}
                colorScheme="gray"
                variant="solid-no-transparent"
                size="xs"
              >
                <SparkelIcon icon={faCut} />
              </MenuButton>
            </Tooltip>
            <MenuList>
              <MenuItem
                icon={<SparkelIcon as={xPlaneIcon} color={gray600} />}
                onClick={() => void setActiveSection(ViewSection.X)}
              >
                {t('viewer-interactions.section-x-plane')}
              </MenuItem>
              <MenuItem
                icon={<SparkelIcon as={yPlaneIcon} color={gray600} />}
                onClick={() => void setActiveSection(ViewSection.Y)}
              >
                {t('viewer-interactions.section-y-plane')}
              </MenuItem>
              <MenuItem
                icon={<SparkelIcon as={zPlaneIcon} color={gray600} />}
                onClick={() => void setActiveSection(ViewSection.Z)}
              >
                {t('viewer-interactions.section-z-plane')}
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </ButtonGroup>
      <Spacer />
      <ButtonGroup>
        {showResetFilterButton ? (
          <>
            <Tooltip label={t('viewer-interactions.clear-filters')}>
              <IconButton
                aria-label="Clear filters"
                icon={<SparkelIcon icon={faTimes} />}
                isRound
                colorScheme="gray"
                variant="solid-no-transparent"
                size="xs"
                onClick={() => {
                  setActiveSection(null);
                  setHiddenElements({});
                  setIsolatedElements({});
                  dispatchViewerFilterAction({ type: ActionKind.ResetView });
                }}
              />
            </Tooltip>
            <Divider borderColor={borderColor} orientation="vertical" />
          </>
        ) : null}

        <IsolatedElementsButton
          numIsolatedElements={numIsolatedElements}
          onClearIsolated={() => setIsolatedElements({})}
        />
        <HiddenElementsButton
          numHiddenElements={numHiddenElements}
          onClearHidden={() => setHiddenElements({})}
        />
        <Menu closeOnSelect={false} isLazy>
          <Tooltip label={t('viewer-interactions.toggle-categories')}>
            <MenuButton>
              <Button
                borderRadius={'3xl'}
                colorScheme={isCategoriesFiltered ? 'teal' : 'gray'}
                variant={
                  isCategoriesFiltered ? 'toggle' : 'solid-no-transparent'
                }
                isActive={isCategoriesFiltered}
                size="xs"
                aria-label="Show/Hide Categories"
                rightIcon={
                  <SparkelIcon
                    icon={faAngleDown}
                    color={
                      isCategoriesFiltered
                        ? activeIconColor
                        : ['gray.600', 'gray.50']
                    }
                  />
                }
              >
                <SparkelIcon
                  color={
                    isCategoriesFiltered
                      ? activeIconColor
                      : ['gray.600', 'gray.50']
                  }
                  icon={faBuilding}
                />
              </Button>
            </MenuButton>
          </Tooltip>
          <Portal>
            <CategoriesMenuListCheckBoxes
              setCategories={setCategories}
              selectedCategories={Array.from(
                viewerFilterState.selectedCategories
              )}
              models={models}
              shapes={renderedShapes}
            />
          </Portal>
        </Menu>
        <Menu closeOnSelect={false} isLazy>
          <Tooltip label={t('viewer-interactions.toggle-floors')}>
            <MenuButton>
              <Button
                borderRadius={'3xl'}
                colorScheme={isLevelsFiltered ? 'teal' : 'gray'}
                variant={isLevelsFiltered ? 'toggle' : 'solid-no-transparent'}
                isActive={isLevelsFiltered}
                size="xs"
                aria-label="Show/Hide Floors"
                rightIcon={
                  <SparkelIcon
                    icon={faAngleDown}
                    color={
                      isLevelsFiltered
                        ? activeIconColor
                        : ['gray.600', 'gray.50']
                    }
                  />
                }
              >
                <SparkelIcon
                  color={
                    isLevelsFiltered ? activeIconColor : ['gray.600', 'gray.50']
                  }
                  icon={faLayerGroup}
                />
              </Button>
            </MenuButton>
          </Tooltip>
          <Portal>
            <LayersMenuListCheckBoxes
              setSelectedLayers={setSelectedLayers}
              selectedLayers={viewerFilterState.selectedLayers}
              models={models}
            />
          </Portal>
        </Menu>
        {isContractor && (
          <Menu closeOnSelect={false} isLazy>
            <Tooltip label={t('viewer-interactions.toggle-statuses')}>
              <MenuButton>
                <Button
                  borderRadius={'3xl'}
                  colorScheme={isStatusFiltered ? 'teal' : 'gray'}
                  variant={isStatusFiltered ? 'toggle' : 'solid-no-transparent'}
                  isActive={isStatusFiltered}
                  size="xs"
                  aria-label="Show/Hide Statuses"
                  rightIcon={
                    <SparkelIcon
                      color={
                        isStatusFiltered
                          ? activeIconColor
                          : ['gray.600', 'gray.50']
                      }
                      icon={faAngleDown}
                    />
                  }
                >
                  <SparkelIcon
                    color={
                      isStatusFiltered
                        ? activeIconColor
                        : ['gray.600', 'gray.50']
                    }
                    icon={faClipboardCheck}
                  />
                </Button>
              </MenuButton>
            </Tooltip>
            <Portal>
              <OrderStatusMenuListCheckBoxes
                selectedOrderStatuses={Array.from(
                  viewerFilterState.selectedElementOrderStatuses
                )}
                setSelectedOrderStatuses={(orderStatuses: string[]) =>
                  dispatchViewerFilterAction({
                    type: ActionKind.NewSelectedOrderStatuses,
                    payload: orderStatuses,
                  })
                }
              />
            </Portal>
          </Menu>
        )}
      </ButtonGroup>
      <Divider mx={2} borderColor={borderColor} orientation="vertical" />
      {isAllModelsLoaded ? <ElementSearchBar /> : null}
    </GridItem>
  ) : null;
}
